import { OrderType } from "@merchstores/shared/components/MerchStore";
import { IMerchStore } from "@merchstores/admin/components/MerchStore";
import { OrderStatus } from "./OrderStatus";
import { fetchShopifyOrders, IShopifyOrdersData } from "./ShopifyOrders";
import { fetchPendingIndividualOrders, IMerchStoreIndividualOrder, IMerchStoreOrder, IOrderAddress, IOrderCustomer } from ".";
import Calculator from "./OrderListCalculator";
import { ACTIVE_ORDER_ID } from "./ActiveOrder";
import BigNumber from "bignumber.js";

export interface ICombinedOrder {
  id: string;
  name: string;
  status: OrderStatus;
  totalPrice: string;
  createdAt: string;
  closedAt?: string;
  email?: string;
  totalTax: string;
  totalShippingPrice: string;
  shippingTitle?: string;
  tags?: Array<string>;
  customer: IOrderCustomer;
  itemsCount?: number;
  shippingAddress?: IOrderAddress;
  billingAddress?: IOrderAddress;
}

export interface ICombinedOrdersList
{
  orders: Array<ICombinedOrder>;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  cursor: string | null;
}

export async function createVirtualCombinedOrder(merchStore: IMerchStore): Promise<ICombinedOrder>
{
  let defaultCustomerName = '';
  const officeAddress = merchStore.officeAddress;
  
  if (officeAddress)
  {
    defaultCustomerName = officeAddress.firstName + ' ' + officeAddress.lastName;
  }

  const pendingIndividualOrders = await fetchPendingIndividualOrders(merchStore.storeCode);
  
  const activeCombinedOrder: ICombinedOrder = {
    id: ACTIVE_ORDER_ID,
    name: "Active",
    status: OrderStatus.pending,
    totalPrice: Calculator.sumOrderListTotal(pendingIndividualOrders).toString(),
    totalShippingPrice: (new BigNumber('0.00')).toString(),
    totalTax: Calculator.sumOrderListTotalTax(pendingIndividualOrders).toString(),
    createdAt: merchStore.nextProcessingDate ? merchStore.nextProcessingDate : '',
    customer: {
      displayName: defaultCustomerName, // defined by admin?
      email: '' // defined by admin?
    },
    itemsCount: Calculator.sumOrderListItemsCount(pendingIndividualOrders).integerValue().toNumber()
  }

  return activeCombinedOrder;
}

const cache = new Map<string, ICombinedOrdersList>();


export async function listCombinedOrders(merchStore: IMerchStore): Promise<ICombinedOrdersList>
{
  const storeCode = merchStore.storeCode;
  const cachedResult = cache.get(storeCode);

  if (cachedResult)
  {
    return cachedResult;
  }

  const activeCombinedOrder = await createVirtualCombinedOrder(merchStore);

  const shopifyOrdersData = await fetchCombinedOrders(merchStore.storeCode);

  const shopifyCombinedOrders: Array<ICombinedOrder> = shopifyOrdersData.orders.map(order => {
    const merchstoreCloseDateTag = order.tags.find(tag => /close_date=\d{4}-\d{2}-\d{2}/.test(tag));
    const closedAt = merchstoreCloseDateTag ? merchstoreCloseDateTag.replace('close_date=', '') : order.closedAt;
    return {
      ...order,
      status: merchstoreCloseDateTag ? OrderStatus.closed : OrderStatus.active,
      closedAt: closedAt
    }
  })

  const allOrders: Array<ICombinedOrder> = [activeCombinedOrder as ICombinedOrder].concat(shopifyCombinedOrders);

  const combinedOrdersData = {
    orders: allOrders,
    hasNextPage: shopifyOrdersData.hasNextPage,
    hasPreviousPage: shopifyOrdersData.hasPreviousPage,
    cursor: shopifyOrdersData.cursor
  };

  cache.set(storeCode, combinedOrdersData);

  return combinedOrdersData;
}

export async function countCombinedOrders(merchStore: IMerchStore): Promise<number>
{
  const ordersData = await listCombinedOrders(merchStore);
  return ordersData.orders.length;
}


export async function sumCombinedOrders(merchStore: IMerchStore): Promise<string>
{
  const ordersData = await listCombinedOrders(merchStore);
  return Calculator.sumOrderListTotal(ordersData.orders).toString();
}

export async function fetchCombinedOrders(storeCode: string): Promise<IShopifyOrdersData>
{
  return fetchShopifyOrders({
    storeCode: storeCode,
    orderType: OrderType.COMBINED,
    sorting: 'dateDesc'
  });
} 

